import * as React from 'react'
import parse from 'html-react-parser'

import { maxAttemptsForGrammarFillInBlank, numberOfBlanksForFillInBlankPrompt, } from '../../libs'
import { TextEditor, Checkbox, } from '../shared'
import { ConceptSelectorWithCheckbox, } from '../internalTools'
import { BlankByBlankGrading, ConceptResult, CueTemplate, } from '../../quill-marking-logic/src/interfaces'

interface Question {
  blankByBlankGrading?: BlankByBlankGrading;
  cues: string[];
  prompt: string;
}

interface FeedbackAndConceptResultBoxProps {
  blankByBlankGrading: BlankByBlankGrading;
  blankKey: string;
  conceptResults: ConceptResult[];
  cue: string;
  editBlankByBlankGrading: (questionID: string, newBlankByBlankGrading: BlankByBlankGrading) => void;
  feedback: string;
  optimal: boolean;
  questionID: string;
  maxAttempts: number;
  isFinalBlank: boolean;
}

interface BlankByBlankGradingPanelProps {
  editBlankByBlankGrading: (questionID: string, newBlankByBlankGrading: BlankByBlankGrading) => void;
  question: Question;
  questionID: string;
}

const blankForCueTemplate = (cue: string): CueTemplate => ({
  cue,
  conceptResults: [],
  feedback: '',
  optimal: false,
})

function blankByBlankGradingTemplate(numberOfBlanks: number, cues: string[], blankAllowed: boolean): BlankByBlankGrading {
  const cuesForArray = blankAllowed ? [...cues, ''] : cues
  const cuesArray = cuesForArray.map(cue => blankForCueTemplate(cue))

  const blankByBlankGrading = {}

  Array.from({ length: numberOfBlanks, }).forEach((_, index) => {
    blankByBlankGrading[`blank${index + 1}`] = cuesArray
  })

  return blankByBlankGrading
}

function mergeBlankByBlankGrading(
  existing: BlankByBlankGrading | undefined,
  numberOfBlanks: number,
  cues: string[],
  blankAllowed: boolean
): BlankByBlankGrading {
  const freshTemplate = blankByBlankGradingTemplate(numberOfBlanks, cues, blankAllowed)

  if (!existing) return freshTemplate

  const merged = {}

  Object.keys(freshTemplate).forEach(blankKey => {
    if (existing[blankKey]) {
      merged[blankKey] = freshTemplate[blankKey].map(freshCueTemplate => {
        const existingCueData = existing[blankKey]?.find(
          (c: CueTemplate) => c.cue === freshCueTemplate.cue
        )
        return existingCueData || freshCueTemplate
      })
    } else {
      merged[blankKey] = freshTemplate[blankKey]
    }
  })

  return merged
}

const cueOrPlaceholderText = (cue) => (cue.length ? cue : '[empty blank]')

const FeedbackAndConceptResultBox: React.FC<FeedbackAndConceptResultBoxProps> = ({ blankByBlankGrading, blankKey, conceptResults, cue, editBlankByBlankGrading, feedback, optimal, questionID, isFinalBlank, maxAttempts}) => {
  const [tempConceptResults, setTempConceptResults] = React.useState(conceptResults)
  const [tempFeedback, setTempFeedback] = React.useState(feedback)
  const [tempOptimal, setTempOptimal] = React.useState(optimal)
  const [inEditMode, setInEditMode] = React.useState(false)

  // Track when we're expecting an auto-populated value to prevent race conditions with TextEditor
  const expectedFeedbackRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    setTempConceptResults(conceptResults)
    setTempFeedback(feedback)
    setTempOptimal(optimal)
  }, [conceptResults, feedback, optimal])

  React.useEffect(() => {
    if (!(tempOptimal && isFinalBlank && tempFeedback.length === 0)) { return }

    const autoPopulatedFeedback = "That's a strong sentence!"
    expectedFeedbackRef.current = autoPopulatedFeedback
    setTempFeedback(autoPopulatedFeedback)
  }, [tempOptimal])

  const handleFeedbackChange = React.useCallback((newFeedback: string) => {
    const isEmpty = !newFeedback || newFeedback === '<br/>' || newFeedback === '<br>'

    // If we're expecting an auto-populated value and got an empty update, ignore it
    if (expectedFeedbackRef.current !== null && isEmpty) {
      return
    }

    // Clear the expectation once we receive a valid update
    expectedFeedbackRef.current = null
    setTempFeedback(newFeedback)
  }, [])

  function openEditMode() { setInEditMode(true) }

  function cancelEdits() {
    setTempConceptResults(conceptResults)
    setTempFeedback(feedback)
    setTempOptimal(optimal)
    setInEditMode(false)
  }

  function saveEdits() {
    const newBlankByBlankGrading = JSON.parse(JSON.stringify(blankByBlankGrading))
    const index = newBlankByBlankGrading[blankKey].findIndex((cueTemplate: CueTemplate) => cueTemplate.cue === cue)
    newBlankByBlankGrading[blankKey][index] = {
      cue,
      conceptResults: tempConceptResults,
      feedback: tempFeedback,
      optimal: tempOptimal,
    }
    editBlankByBlankGrading(questionID, newBlankByBlankGrading, { blankKey, cue })
    setInEditMode(false)
  }

  function reset() {
    if (!window.confirm('Are you sure you want to delete the feedback and concept results for the response?')) { return }
    const newBlankByBlankGrading = JSON.parse(JSON.stringify(blankByBlankGrading))
    const index = newBlankByBlankGrading[blankKey].findIndex((cueTemplate: CueTemplate) => cueTemplate.cue === cue)
    newBlankByBlankGrading[blankKey][index] = blankForCueTemplate(cue)
    editBlankByBlankGrading(questionID, newBlankByBlankGrading, { blankKey, cue })
  }

  function handleClickOptimalCheckbox() { setTempOptimal(!tempOptimal) }

  function deleteConceptResult(conceptUID: string) {
    const newTempConceptResults = conceptResults.filter(conceptResult => conceptResult.conceptUID !== conceptUID)
    setTempConceptResults(newTempConceptResults)
  }

  function toggleConceptResultCorrect(conceptUID: string, correct: boolean) {
    const newTempConceptResults = [...tempConceptResults]
    const index = newTempConceptResults.findIndex(conceptResult => conceptResult.conceptUID === conceptUID)
    newTempConceptResults[index].correct = !correct

    setTempConceptResults(newTempConceptResults)
  }

  function addNewConceptResult(e: { label: string; value: string }) {
    setTempConceptResults([...tempConceptResults, { name: e.label, conceptUID: e.value, correct: tempOptimal, }])
  }

  let feedbackOrPlaceholder = inEditMode ? <TextEditor handleTextChange={handleFeedbackChange} id="feedback" key="feedback" text={tempFeedback} /> : parse(feedback)

  if (!isFinalBlank && tempOptimal) {
    feedbackOrPlaceholder = <p className="text-editor-placeholder">Optimal feedback unnecessary until final blank.</p>
  }

  if (maxAttempts === 1 && !tempOptimal) {
    feedbackOrPlaceholder = <p className="text-editor-placeholder">Suboptimal feedback unnecessary for questions with only one attempt.</p>
  }

  if (inEditMode) {

    return (
      <section className="feedback-and-concept-result-box">
        <h3>Modify feedback for <span className="cue-yellow-background">{cueOrPlaceholderText(cue)}</span></h3>
        <label className="label" htmlFor="feedback">
          Feedback
        </label>
        {feedbackOrPlaceholder}
        <Checkbox label="OPTIMAL" onClick={handleClickOptimalCheckbox} selected={tempOptimal} />
        <h3>Add concept results for <span className="cue-yellow-background">{cueOrPlaceholderText(cue)}</span></h3>
        {tempConceptResults.map(conceptResult => {
          const { conceptUID, correct, } = conceptResult
          return (
            <ConceptSelectorWithCheckbox
              checked={correct}
              currentConceptUID={conceptUID}
              deleteConceptResult={() => deleteConceptResult(conceptUID)}
              fillInTheBlank={true}
              handleSelectorChange={() => {}}
              key={conceptUID}
              onCheckboxChange={() => toggleConceptResultCorrect(conceptUID, correct)}
              selectorDisabled={true}
            />
          )
        })}
        <ConceptSelectorWithCheckbox
          checked={tempOptimal}
          currentConceptUID={null}
          deleteConceptResult={() => {}}
          fillInTheBlank={true}
          handleSelectorChange={addNewConceptResult}
          hideConceptFeedback={true}
          key='new'
          onCheckboxChange={() => {}}
          selectorDisabled={false}
        />
        <div className="button-wrapper">
          <button className="quill-button medium outlined green focus-on-light" onClick={cancelEdits} type="button">Cancel</button>
          <button className="quill-button medium outlined green focus-on-light" disabled={!tempConceptResults.length} onClick={saveEdits} type="button">{!tempConceptResults.length ? 'Add Concept Result to Save' : 'Save'}</button>
        </div>
      </section>
    )
  }

  return (
    <section className="feedback-and-concept-result-box">
      <h3>Feedback and Concept Results for <span className="cue-yellow-background">{cueOrPlaceholderText(cue)}</span></h3>
      <p>
        <strong>Feedback:</strong>&nbsp;
        {feedbackOrPlaceholder}
      </p>
      <p><strong>Concept Results:</strong></p>
      <ul>
        {conceptResults.map(conceptResult => (
          <li className="concept-result" key={conceptResult.conceptUID}>
            <span>{conceptResult.name}</span>
            {conceptResult.correct ? <span className="tag is-small is-success">Correct</span> : <span className="tag is-small is-danger">Incorrect</span>}
          </li>
        ))}
      </ul>
      <div className="button-wrapper">
        <button className="quill-button medium outlined green focus-on-light" onClick={openEditMode} type="button">Edit</button>
        <button className="quill-button medium outlined green focus-on-light" onClick={reset} type="button">Delete</button>
      </div>
    </section>
  )
}

const BlankByBlankGradingPanel: React.FC<BlankByBlankGradingPanelProps> = ({ editBlankByBlankGrading, question, questionID, }) => {
  const [selectedBlank, setSelectedBlank] = React.useState(1)

  const { cues, prompt, blankByBlankGrading, } = question

  const numberOfBlanks = numberOfBlanksForFillInBlankPrompt(prompt)

  const tempBlankByBlankGrading = mergeBlankByBlankGrading(blankByBlankGrading, numberOfBlanks, cues, question.blankAllowed)
  const maxAttempts = maxAttemptsForGrammarFillInBlank(question.cues.length, numberOfBlanks, question.blankAllowed)

  const blankOptionButtons = Array.from({ length: numberOfBlanks, }).map((_, index) => {
    const blankNumber = index + 1
    const isSelectedBlank = selectedBlank === blankNumber
    const cueTemplatesForBlank = tempBlankByBlankGrading[`blank${blankNumber}`]

    const optimalTemplate = cueTemplatesForBlank.find(cueTemplate => cueTemplate.optimal)
    const allTemplatesHaveConceptResults = cueTemplatesForBlank.every(cueTemplate => cueTemplate.conceptResults.length)
    const optimalTemplateHasFeedbackIfFinalBlank = blankNumber === numberOfBlanks ? optimalTemplate?.feedback : true
    const suboptimalBlanksHaveFeedbackIfMultipleAttemptsPossible = maxAttempts > 1 ? cueTemplatesForBlank.filter(ct => !ct.optimal).every(ct => ct.feedback) : true

    const isFullyGraded = optimalTemplate && allTemplatesHaveConceptResults && optimalTemplateHasFeedbackIfFinalBlank && suboptimalBlanksHaveFeedbackIfMultipleAttemptsPossible

    function changeSelectedBlank () {
      setSelectedBlank(blankNumber)
    }

    let className = 'quill-button medium focus-on-light outlined'

    className += isSelectedBlank ? ' green' : ' grey'
    className += isFullyGraded ? ' fully-graded' : ' not-yet-graded'

    return (
      <button className={className} key={blankNumber} onClick={changeSelectedBlank} type="button">Blank {blankNumber}</button>
    )
  })

  const feedbackAndConceptResultBoxes = tempBlankByBlankGrading[`blank${selectedBlank}`].map(boxData => {
    const { cue, conceptResults, feedback, optimal, } = boxData

    return (
      <FeedbackAndConceptResultBox
        blankByBlankGrading={tempBlankByBlankGrading}
        blankKey={`blank${selectedBlank}`}
        conceptResults={conceptResults}
        cue={cue}
        editBlankByBlankGrading={editBlankByBlankGrading}
        feedback={feedback}
        isFinalBlank={selectedBlank === numberOfBlanks}
        key={`${selectedBlank}-${cue}`}
        maxAttempts={maxAttempts}
        optimal={optimal}
        questionID={questionID}
      />
    )
  })

  return (
    <section className="blank-by-blank-grading-panel">
      <div className="blank-option-buttons">{blankOptionButtons}</div>
      <div className="feedback-and-concept-result-boxes">{feedbackAndConceptResultBoxes}</div>
    </section>
  )
}

export { BlankByBlankGradingPanel, }
